import React from 'react'
import { Edit, useForm } from '@refinedev/antd'
import { Form, Select, Input, Typography } from 'antd'
import type { Tables } from '@muaa/shared'

// Embedded profile shape (names) for display-only context.
type RelProfile = Pick<Tables<'profiles'>, 'name'>
type Relationship = Tables<'mentorship_relationships'> & {
  mentor: RelProfile | null
  mentee: RelProfile | null
}

const { Text } = Typography

// Full lifecycle vocabulary (a DB CHECK constraint enforces this set). This is
// the admin escalation/closure screen — staff can force any status, including
// closing out a relationship that should have ended.
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'ended', label: 'Ended' },
]

const CADENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
]

// FK embeds: pull the mentor/mentee names so staff know which pairing they're
// editing. The update only writes the editable fields below.
const SELECT =
  '*, mentor:profiles!mentorship_relationships_mentor_profile_fkey(name), mentee:profiles!mentorship_relationships_mentee_profile_fkey(name)'

export function MentorshipRelationshipEdit() {
  const { formProps, saveButtonProps, query } = useForm<Relationship>({
    meta: { select: SELECT },
  })
  const record = query?.data?.data

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        {/* Read-only context — the parties are fixed; only the lifecycle below is editable. */}
        <Form.Item label="Mentor">
          <Input value={record?.mentor?.name ?? record?.mentor_id ?? '—'} disabled />
        </Form.Item>

        <Form.Item label="Mentee">
          <Input value={record?.mentee?.name ?? record?.mentee_id ?? '—'} disabled />
        </Form.Item>

        {/* Editable lifecycle fields. */}
        <Form.Item label="Status" name="status" rules={[{ required: true }]}>
          <Select options={STATUS_OPTIONS} placeholder="Select status" />
        </Form.Item>

        <Form.Item label="Focus" name="focus">
          <Input placeholder="e.g. Career transition into product management" />
        </Form.Item>

        <Form.Item label="Cadence" name="cadence">
          <Select options={CADENCE_OPTIONS} placeholder="Select cadence" allowClear />
        </Form.Item>

        <Form.Item label="Completion Note" name="completion_note">
          <Input.TextArea
            placeholder="Why this relationship was paused, completed, or ended"
            autoSize={{ minRows: 2 }}
          />
        </Form.Item>

        <Text type="secondary">
          Admin override — editing here updates the relationship directly for staff escalation or closure and does
          not notify either member.
        </Text>
      </Form>
    </Edit>
  )
}
